import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { listReservationsInRange } from "@/lib/admin.functions";
import { dublinWallTimeToIso } from "@/lib/dublin-time";
import { Button } from "@/components/ui/button";
import { ReservationNotes } from "@/components/admin/ReservationNotes";
import {
  ReservationSourceBadge,
  bookedByName,
} from "@/components/admin/ReservationSourceBadge";
import {
  EditReservationDialog,
  type EditableReservation,
} from "@/components/admin/EditReservationDialog";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

export const Route = createFileRoute("/_authenticated/admin/calendar")({
  component: CalendarPage,
});

type View = "month" | "week" | "day";

/* ---------- Dublin date helpers ---------- */
const TZ = "Europe/Dublin";

function ymd(d: Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(d);
}

/** UTC ISO for the start of a Dublin local day. */
function dayStartIso(y: string): string {
  return dublinWallTimeToIso(y, "00:00");
}

function shift(y: string, days: number): string {
  const d = new Date(`${y}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function shiftMonth(y: string, months: number): string {
  const d = new Date(`${y}T12:00:00Z`);
  d.setUTCDate(1);
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}

/** Monday-based start of the week containing ymd. */
function weekStart(y: string): string {
  const d = new Date(`${y}T12:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7;
  return shift(y, -dow);
}

function monthStart(y: string): string {
  return `${y.slice(0, 7)}-01`;
}

function monthDays(y: string): number {
  const d = new Date(`${monthStart(y)}T12:00:00Z`);
  d.setUTCMonth(d.getUTCMonth() + 1);
  d.setUTCDate(0);
  return d.getUTCDate();
}

function timeLabel(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: TZ,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(new Date(iso));
}

function dayLabel(y: string, opts?: Intl.DateTimeFormatOptions): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    ...(opts ?? { weekday: "short", day: "numeric", month: "short" }),
  }).format(new Date(`${y}T12:00:00Z`));
}

const STATUS_STYLE: Record<string, string> = {
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  pending_approval: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  pending_payment: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  seated: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  checked_in: "bg-sky-500/15 text-sky-300 border-sky-500/30",
  completed: "bg-muted text-muted-foreground border-border",
  cancelled: "bg-destructive/15 text-destructive border-destructive/30",
  no_show: "bg-destructive/15 text-destructive border-destructive/30",
  unavailable: "bg-muted text-muted-foreground border-border",
};

type Row = Awaited<ReturnType<typeof listReservationsInRange>>[number];

function tableLabels(r: Row): string {
  const links = (r as { reservation_tables?: { tables?: { label?: string } | null }[] }).reservation_tables ?? [];
  const fromLinks = links.map((l) => l.tables?.label).filter(Boolean) as string[];
  const main = (r as { tables?: { label?: string } | null }).tables?.label;
  const all = Array.from(new Set([...(main ? [main] : []), ...fromLinks]));
  return all.join(", ");
}

function guestName(r: Row): string {
  const g = (r as { guests?: { full_name?: string } | { full_name?: string }[] | null }).guests;
  const one = Array.isArray(g) ? g[0] : g;
  return one?.full_name ?? "Guest";
}

function CalendarPage() {
  const qc = useQueryClient();
  const fetchRange = useServerFn(listReservationsInRange);
  const [view, setView] = useState<View>("month");
  const [cursor, setCursor] = useState<string>(() => ymd(new Date()));
  const [selected, setSelected] = useState<Row | null>(null);
  const [editing, setEditing] = useState<EditableReservation | null>(null);

  const { from, to, days } = useMemo(() => {
    if (view === "day") {
      return { from: cursor, to: shift(cursor, 1), days: [cursor] };
    }
    if (view === "week") {
      const s = weekStart(cursor);
      return {
        from: s,
        to: shift(s, 7),
        days: Array.from({ length: 7 }, (_, i) => shift(s, i)),
      };
    }
    const s = monthStart(cursor);
    const n = monthDays(cursor);
    return {
      from: s,
      to: shift(s, n),
      days: Array.from({ length: n }, (_, i) => shift(s, i)),
    };
  }, [view, cursor]);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin-calendar", from, to],
    queryFn: () => fetchRange({ data: { from: dayStartIso(from), to: dayStartIso(to) } }),
  });

  // Live sync with the database
  useEffect(() => {
    const ch = supabase
      .channel("admin-calendar-sync")
      .on("postgres_changes", { event: "*", schema: "public", table: "reservations" }, () => {
        qc.invalidateQueries({ queryKey: ["admin-calendar"] });
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "reservation_tables" }, () => {
        qc.invalidateQueries({ queryKey: ["admin-calendar"] });
      })
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, [qc]);

  const byDay = useMemo(() => {
    const map: Record<string, Row[]> = {};
    const seen = new Set<string>();
    for (const r of rows) {
      // A booking belongs to exactly one day and is drawn exactly once.
      if (seen.has(r.id as string)) continue;
      seen.add(r.id as string);
      const key = ymd(new Date(r.starts_at as string));
      (map[key] ??= []).push(r);
    }
    return map;
  }, [rows]);

  const totals = useMemo(() => {
    const active = rows.filter((r) => !["cancelled", "no_show"].includes(r.status as string));
    return {
      bookings: active.length,
      covers: active.reduce((s, r) => s + (r.party_size ?? 0), 0),
      pending: rows.filter((r) => (r.status as string).startsWith("pending")).length,
    };
  }, [rows]);

  function step(dir: number) {
    if (view === "month") setCursor(shiftMonth(cursor, dir));
    else if (view === "week") setCursor(shift(cursor, dir * 7));
    else setCursor(shift(cursor, dir));
  }

  const heading =
    view === "month"
      ? dayLabel(cursor, { month: "long", year: "numeric" })
      : view === "week"
        ? `${dayLabel(days[0])} – ${dayLabel(days[6])}`
        : dayLabel(cursor, { weekday: "long", day: "numeric", month: "long", year: "numeric" });

  const today = ymd(new Date());

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Calendar</h1>
          <p className="text-sm text-muted-foreground">
            {heading} · {totals.bookings} bookings · {totals.covers} covers
            {totals.pending ? ` · ${totals.pending} pending` : ""}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex rounded-md border border-border p-0.5">
            {(["month", "week", "day"] as View[]).map((v) => (
              <button
                key={v}
                onClick={() => setView(v)}
                className={`rounded px-3 py-1 text-xs capitalize transition ${
                  view === v ? "bg-primary/15 text-primary" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
          <Button variant="outline" size="sm" onClick={() => step(-1)} aria-label="Previous">
            ‹
          </Button>
          <Button variant="outline" size="sm" onClick={() => setCursor(today)}>
            Today
          </Button>
          <Button variant="outline" size="sm" onClick={() => step(1)} aria-label="Next">
            ›
          </Button>
        </div>
      </header>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Loading reservations…</p>
      ) : view === "month" ? (
        <MonthGrid days={days} byDay={byDay} today={today} onPick={(d) => { setCursor(d); setView("day"); }} onSelect={setSelected} />
      ) : view === "week" ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7">
          {days.map((d) => (
            <DayColumn key={d} ymdStr={d} rows={byDay[d] ?? []} today={today} onSelect={setSelected} />
          ))}
        </div>
      ) : (
        <DayColumn ymdStr={cursor} rows={byDay[cursor] ?? []} today={today} onSelect={setSelected} expanded />
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-md">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {guestName(selected)} · {selected.reference}
                </DialogTitle>
              </DialogHeader>
              <div className="flex flex-wrap items-center gap-2">
                <ReservationSourceBadge source={(selected as { source?: string }).source} />
                {bookedByName(selected) && (
                  <span className="text-xs text-muted-foreground">
                    by {bookedByName(selected)}
                  </span>
                )}
              </div>
              <dl className="space-y-2 text-sm">
                <Item label="When" value={`${dayLabel(ymd(new Date(selected.starts_at as string)), { weekday: "short", day: "numeric", month: "short" })} · ${timeLabel(selected.starts_at as string)} (${selected.duration_minutes} min)`} />
                <Item label="Party" value={`${selected.party_size} guests`} />
                <Item label="Type" value={String(selected.booking_type).replace(/_/g, " ")} />
                <Item label="Status" value={String(selected.status).replace(/_/g, " ")} />
                <Item label="Tables" value={tableLabels(selected) || "Not assigned"} />
                {selected.preferred_area && <Item label="Area" value={selected.preferred_area} />}
                {(selected as { created_at?: string }).created_at && (
                  <Item
                    label="Taken on"
                    value={new Date((selected as { created_at: string }).created_at).toLocaleString("en-IE", {
                      timeZone: TZ,
                      day: "2-digit",
                      month: "short",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  />
                )}
              </dl>
              <ReservationNotes
                reservationId={selected.id as string}
                initialNotes={selected.notes as string | null}
                onSaved={(notes) => setSelected({ ...selected, notes } as Row)}
              />
              <div className="flex justify-end">
                <Button
                  onClick={() => {
                    const g = (selected as { guests?: { full_name?: string; phone?: string; email?: string } | { full_name?: string; phone?: string; email?: string }[] | null }).guests;
                    const one = Array.isArray(g) ? g[0] : g;
                    setEditing({
                      id: selected.id as string,
                      reference: selected.reference as string,
                      booking_type: String(selected.booking_type),
                      starts_at: selected.starts_at as string,
                      duration_minutes: selected.duration_minutes as number,
                      party_size: selected.party_size as number,
                      notes: (selected.notes as string | null) ?? null,
                      preferred_area: (selected.preferred_area as string | null) ?? null,
                      guest_full_name: one?.full_name ?? null,
                      guest_phone: one?.phone ?? null,
                      guest_email: one?.email ?? null,
                    });
                    setSelected(null);
                  }}
                >
                  Edit booking
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <EditReservationDialog reservation={editing} onClose={() => setEditing(null)} />
    </div>
  );
}

function Item({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-4 border-b border-border/50 pb-1">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="text-right">{value}</dd>
    </div>
  );
}

function MonthGrid({
  days,
  byDay,
  today,
  onPick,
  onSelect,
}: {
  days: string[];
  byDay: Record<string, Row[]>;
  today: string;
  onPick: (d: string) => void;
  onSelect: (r: Row) => void;
}) {
  const lead = (new Date(`${days[0]}T12:00:00Z`).getUTCDay() + 6) % 7;
  return (
    <div className="grid grid-cols-7 gap-1 sm:gap-2">
      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
        <div key={d} className="pb-1 text-center text-[10px] uppercase tracking-widest text-muted-foreground">
          {d}
        </div>
      ))}
      {Array.from({ length: lead }, (_, i) => (
        <div key={`pad-${i}`} />
      ))}
      {days.map((d) => {
        const list = byDay[d] ?? [];
        const covers = list.reduce((s, r) => s + (r.party_size ?? 0), 0);
        return (
          <div
            key={d}
            className={`min-h-[86px] rounded-md border p-1.5 text-left transition sm:min-h-[110px] ${
              d === today ? "border-primary/50 bg-primary/5" : "border-border bg-card/40"
            }`}
          >
            <button
              onClick={() => onPick(d)}
              className="mb-1 flex w-full items-center justify-between text-xs text-muted-foreground hover:text-foreground"
            >
              <span className={d === today ? "font-semibold text-primary" : ""}>{d.slice(8)}</span>
              {list.length > 0 && <span className="text-[10px]">{covers} cvrs</span>}
            </button>
            <div className="space-y-1">
              {list.slice(0, 3).map((r) => (
                <button
                  key={r.id}
                  onClick={() => onSelect(r)}
                  className={`block w-full truncate rounded border px-1 py-0.5 text-left text-[10px] ${
                    STATUS_STYLE[r.status as string] ?? "border-border bg-muted"
                  }`}
                >
                  {timeLabel(r.starts_at as string)} {guestName(r)} ·{r.party_size}
                </button>
              ))}
              {list.length > 3 && (
                <button onClick={() => onPick(d)} className="text-[10px] text-muted-foreground hover:text-foreground">
                  +{list.length - 3} more
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function DayColumn({
  ymdStr,
  rows,
  today,
  onSelect,
  expanded,
}: {
  ymdStr: string;
  rows: Row[];
  today: string;
  onSelect: (r: Row) => void;
  expanded?: boolean;
}) {
  const covers = rows.reduce((s, r) => s + (r.party_size ?? 0), 0);
  return (
    <div className={`rounded-lg border p-3 ${ymdStr === today ? "border-primary/50 bg-primary/5" : "border-border bg-card/40"}`}>
      <div className="mb-2 flex items-baseline justify-between">
        <h2 className="text-sm font-semibold">{dayLabel(ymdStr)}</h2>
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
          {rows.length} · {covers} cvrs
        </span>
      </div>
      {rows.length === 0 ? (
        <p className="text-xs text-muted-foreground">No reservations.</p>
      ) : (
        <ul className="space-y-1.5">
          {rows.map((r) => (
            <li key={r.id}>
              <button
                onClick={() => onSelect(r)}
                className={`w-full rounded-md border px-2 py-1.5 text-left text-xs transition hover:opacity-90 ${
                  STATUS_STYLE[r.status as string] ?? "border-border bg-muted"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium">
                    {timeLabel(r.starts_at as string)} · {guestName(r)}
                  </span>
                  <span>{r.party_size}p</span>
                </div>
                {expanded && (
                  <div className="mt-0.5 text-[10px] opacity-80">
                    {String(r.booking_type).replace(/_/g, " ")}
                    {tableLabels(r) ? ` · ${tableLabels(r)}` : " · no table"} ·{" "}
                    {String(r.status).replace(/_/g, " ")}
                  </div>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
